import type { PaginatedStories, StoryItem, StoryDetail } from '../types/api'
import { authFetch } from './apiClient'

const BASE = '/newspaper/api/v1'

/**
 * Canonical TanStack Query key for the top-stories endpoint.
 *
 * Normalizes values so that NewspaperPage and StoryCarousel share the same key
 * and TanStack Query deduplicates the in-flight request when both mount at once.
 *
 * - category: undefined / 'all' → 'all'
 * - date: undefined (today) → ''
 */
export function topStoriesQueryKey(
  citySlug: string,
  langCode: string,
  category: string | undefined,
  date: string | undefined,
): readonly unknown[] {
  return ['stories-top', citySlug, langCode, category ?? 'all', date ?? '']
}

export async function fetchTopStories(
  citySlug: string,
  langCode: string,
  category?: string,
  days = 7,
  limit = 10,
  date?: string,
): Promise<StoryItem[]> {
  const params = new URLSearchParams({
    city_slug: citySlug,
    lang_code: langCode,
    limit: String(limit),
  })
  if (date) {
    params.set('date', date)
  } else {
    params.set('days', String(days))
  }
  if (category) params.set('category', category)
  const res = await authFetch(`${BASE}/stories/top/?${params}`)
  if (!res.ok) throw new Error(`stories/top API error: ${res.status}`)
  const data = (await res.json()) as { stories: StoryItem[] }
  return data.stories
}

export async function fetchStories(
  citySlug: string,
  langCode: string,
  category: string | undefined,
  page: number,
  pageSize = 10,
  days = 7,
  date?: string,
): Promise<PaginatedStories> {
  const params = new URLSearchParams({
    city_slug: citySlug,
    lang_code: langCode,
    page: String(page),
    page_size: String(pageSize),
  })
  if (date) {
    params.set('date', date)
  } else {
    params.set('days', String(days))
  }
  if (category) params.set('category', category)
  const res = await authFetch(`${BASE}/stories/?${params}`)
  if (!res.ok) throw new Error(`stories API error: ${res.status}`)
  return res.json() as Promise<PaginatedStories>
}

export async function fetchSimilarStories(
  clusterId: number,
  langCode: string,
  limit = 5,
  days = 7,
  date?: string,
): Promise<StoryItem[]> {
  const params = new URLSearchParams({ lang_code: langCode, limit: String(limit), days: String(days) })
  if (date) params.set('date', date)
  const res = await authFetch(`${BASE}/stories/${clusterId}/similar/?${params}`)
  if (!res.ok) throw new Error(`stories/similar API error: ${res.status}`)
  const data = await res.json() as { stories: StoryItem[] }
  return data.stories
}

export async function fetchStory(
  clusterId: number,
  langCode: string,
): Promise<StoryDetail> {
  const params = new URLSearchParams({ lang_code: langCode })
  const res = await authFetch(`${BASE}/stories/${clusterId}/?${params}`)
  if (!res.ok) throw new Error(`story API error: ${res.status}`)
  return res.json() as Promise<StoryDetail>
}

/**
 * Canonical TanStack Query key for the low-impact stories endpoint.
 */
export function lowImpactStoriesQueryKey(
  citySlug: string,
  langCode: string,
  category: string | undefined,
  date: string | undefined,
): readonly unknown[] {
  return ['stories-low-impact', citySlug, langCode, category ?? 'all', date ?? '']
}

export async function fetchLowImpactStories(
  citySlug: string,
  langCode: string,
  category?: string,
  date?: string,
): Promise<StoryItem[]> {
  const params = new URLSearchParams({
    city_slug: citySlug,
    lang_code: langCode,
  })
  if (date) params.set('date', date)
  if (category) params.set('category', category)
  const res = await authFetch(`${BASE}/stories/low-impact/?${params}`)
  if (!res.ok) throw new Error(`stories/low-impact API error: ${res.status}`)
  const data = (await res.json()) as { stories: StoryItem[] }
  return data.stories
}
